import React, { useState, useEffect } from "react";
import {
  Button,
  Table,
  Modal,
  Input,
  InputNumber,
  message,
  Popconfirm,
  Space,
  Form,
  Tag,
  Upload,
} from "antd";
import S3Image from "../../../component/S3Image";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  InboxOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { GET, POST, PUT, DELETE } from "../../../utils/apirequest";
import { API } from "../../../config/API";
import Loadingbox from "../../../component/loadingbox";
import type { ColumnsType } from "antd/es/table";
import type { RcFile, UploadFile } from "antd/es/upload/interface";
import axios from "../../../config/axiosInstance";
import { useSelector } from "react-redux";
import moment from "moment";

const { TextArea } = Input;
const { Dragger } = Upload;

interface Photo {
  id: number;
  title: string;
  file: string;
  is_image: boolean;
  createdAt: string;
  updatedAt: string;
  attributes?: {
    title: string;
    file: string;
    is_image: boolean;
    createdAt: string;
    updatedAt: string;
  };
}

interface BannerTabProps {
  isActive?: boolean;
}

const BannerTab: React.FC<BannerTabProps> = ({ isActive = true }) => {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [uploading, setUploading] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<any>("");
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [form] = Form.useForm();
  const User = useSelector((state: any) => state?.Auth);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  // Call API when tab becomes active or pagination changes
  useEffect(() => {
    if (isActive) {
      loadPhotos();
    }
  }, [isActive, currentPage, pageSize]);

  const loadPhotos = async () => {
    try {
      setLoading(true);

      // Build API URL with required filters and pagination
      const params = new URLSearchParams({
        "filters[is_deleted][$eq]": "false",
        "filters[is_image][$eq]": "true",
        "filters[type][$eq]": "banner",
        "sort[0]": "order:asc",
        "sort[1]": "createdAt:desc",
        "pagination[page]": currentPage.toString(),
        "pagination[pageSize]": pageSize.toString(),
      });

      const apiUrl = `${API.SPECIAL_PAGE_MEDIA_LIST}?${params.toString()}`;
      console.log("Banner API URL:", apiUrl); // Debug log

      const response: any = await GET(apiUrl, null, null);
      console.log("Banner API Response:", response); // Debug log

      if (response) {
        // Handle Strapi v4 response structure
        let photosData = [];
        let totalCount = 0;

        if (response.data) {
          photosData = Array.isArray(response.data)
            ? response.data
            : [response.data];

          // Extract pagination info
          if (response.meta?.pagination) {
            totalCount = response.meta.pagination.total;
            setTotal(totalCount);
          } else {
            setTotal(photosData.length);
          }
        } else if (Array.isArray(response)) {
          photosData = response;
          setTotal(photosData.length);
        } else if (response.attributes) {
          photosData = [response];
          setTotal(1);
        }

        console.log("Banner data:", photosData); // Debug log
        console.log("Total count:", totalCount); // Debug log
        setPhotos(photosData);
      } else {
        console.log("No response received");
        setPhotos([]);
        setTotal(0);
      }
    } catch (error: any) {
      console.error("Error loading banners:", error);
      console.error("Error details:", error.response || error.message);
      message.error("Failed to load banners");
      setPhotos([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditMode(false);
    setSelectedPhoto(null);
    setSelectedImageUrl("");
    setFileList([]);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (photo: Photo) => {
    setEditMode(true);
    setSelectedPhoto(photo);
    const photoData: any = photo.attributes || photo;
    // Fix image prefilling - set as object structure to match display/save expectations
    setSelectedImageUrl({ imageUrl: photoData.file });
    setFileList([]);
    form.setFieldsValue({
      description: photoData.title,
      order: photoData.order || 0,
    });
    setModalVisible(true);
  };

  // Upload image using the correct form-data structure as per image-upload.md
  const uploadImageToServer = async (
    file: RcFile,
    description: string
  ): Promise<string> => {
    try {
      setUploading(true);

      const formData = new FormData();
      formData.append("image", file);

      const config = {
        method: "post",
        maxBodyLength: Infinity,
        url: API.NEW_IMAGE_UPLOAD,
        headers: {
          "Content-Type": "multipart/form-data",
        },
        data: formData,
      };

      const response = await axios.request(config);

      if (response && response.data) {
        message.success("Image uploaded successfully!");
        return response.data;
      } else {
        throw new Error("Upload failed");
      }
    } catch (error) {
      console.error("Upload error:", error);
      message.error("Failed to upload image. Please try again");
      throw error;
    } finally {
      setUploading(false);
    }
  };

  // Handle file upload
  const handleFileUpload = async (file: RcFile) => {
    // Validate file type
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error("You can only upload image files!");
      return false;
    }

    // Validate file size (limit to 5MB)
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error("Image must be smaller than 5MB!");
      return false;
    }

    // Get current description from form
    const description = form.getFieldValue("description") || "New Photo";

    try {
      const imageUrl = await uploadImageToServer(file, description);
      setSelectedImageUrl(imageUrl);

      // Create preview for the uploaded file
      const reader = new FileReader();
      reader.onload = () => {
        setFileList([
          {
            uid: "-1",
            name: file.name,
            status: "done",
            url: reader.result as string,
          },
        ]);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      // Error message already shown in uploadImageToServer
    }

    return false; // Prevent default upload behavior
  };

  // Handle file removal
  const handleFileRemove = () => {
    setSelectedImageUrl("");
    setFileList([]);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      if (!selectedImageUrl) {
        message.error("Please select an image");
        return;
      }

      const photoData = {
        data: {
          title: values.description || "",
          file: selectedImageUrl?.imageUrl,
          is_image: true,
          is_deleted: false,
          type: "banner",
          order: values.order || 0,
          caption: values.caption || "",
          created_by: User?.user?.id,
        },
      };

      let response: any;
      if (editMode && selectedPhoto) {
        response = await PUT(
          `${API.SPECIAL_PAGE_MEDIA_EDIT}${selectedPhoto.id}`,
          photoData
        );
      } else {
        response = await POST(API.SPECIAL_PAGE_MEDIA_ADD, photoData);
      }

      if (response?.data) {
        message.success(
          editMode
            ? "Banner updated successfully!"
            : "Banner added successfully!"
        );
        // Reset to first page and reload
        setCurrentPage(1);
        loadPhotos();
        setModalVisible(false);
        form.resetFields();
        setSelectedImageUrl("");
      }
    } catch (error) {
      console.error("Error saving photo:", error);
      message.error("Failed to save photo");
    }
  };

  const handleDelete = async (photoId: number) => {
    try {
      const response = await DELETE(
        `${API.SPECIAL_PAGE_MEDIA_DELETE}${photoId}`
      );
      if (response) {
        message.success("Banner deleted successfully!");
        // If current page has no items after deletion, go to previous page
        if (photos.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          loadPhotos();
        }
      }
    } catch (error) {
      console.error("Error deleting banner:", error);
      message.error("Failed to delete banner");
    }
  };

  // Handle pagination changes
  const handleTableChange = (page: number, size?: number) => {
    setCurrentPage(page);
    if (size && size !== pageSize) {
      setPageSize(size);
      setCurrentPage(1); // Reset to first page when page size changes
    }
  };

  // Handle manual refresh
  const handleRefresh = () => {
    setCurrentPage(1);
    loadPhotos();
  };

  const columns: ColumnsType<Photo> = [
    {
      title: "Preview",
      dataIndex: "file",
      key: "preview",
      width: 100,
      render: (file: string, record: Photo) => {
        const photoData = record.attributes || record;
        return (
          <S3Image
            src={photoData.file}
            alt={photoData.title}
            width={60}
            height={60}
            style={{ objectFit: "cover", borderRadius: 4 }}
            preview={{
              mask: <EyeOutlined />,
            }}
          />
        );
      },
    },
    // {
    //   title: "Title/Description",
    //   dataIndex: "title",
    //   key: "title",
    //   render: (title: string, record: Photo) => {
    //     const photoData = record.attributes || record;
    //     return (
    //       <div>
    //         <div style={{ fontWeight: 500 }}>
    //           {photoData.title || "Untitled"}
    //         </div>
    //         <div style={{ fontSize: "12px", color: "#666" }}>Image File</div>
    //       </div>
    //     );
    //   },
    // },

    // {
    //   title: "Created",
    //   dataIndex: "createdAt",
    //   key: "createdAt",
    //   width: 200,
    //   render: (date: string, record: Photo) => {
    //     const photoData = record.attributes || record;
    //     let _date = moment(photoData.createdAt).format("DD-MM-YYYY HH:MM A");
    //     // return new Date(photoData.createdAt).toLocaleDateString();
    //     return _date;
    //   },
    // },

    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 200,
      render: (date: string, record: Photo) => {
        const photoData = record.attributes || record;
        let _date = moment(photoData.createdAt).format("DD-MM-YYYY HH:MM A");
        return _date;
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record: Photo) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this photo?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />} size="small">
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading) {
    return <Loadingbox />;
  }

  return (
    <div>
      <br />
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span style={{ color: "#666" }}>Total Banners: {total}</span>
        <div>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add New Banner
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={loading}
            style={{ marginLeft: 8 }}
          >
            Refresh
          </Button>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={photos}
        rowKey="id"
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} banners`,
          onChange: handleTableChange,
          onShowSizeChange: (current, size) => handleTableChange(current, size),
          pageSizeOptions: ["5", "10", "20", "50"],
        }}
        locale={{
          emptyText: "No banners found. Click 'Add New Banner' to get started.",
        }}
        loading={loading}
      />

      {/* Add/Edit Photo Modal */}
      <Modal
        title={editMode ? "Edit Banner" : "Add New Banner"}
        visible={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setSelectedImageUrl("");
          setFileList([]);
        }}
        okText="Save"
        cancelText="Cancel"
        width={600}
        confirmLoading={uploading}
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Image" required>
            {!selectedImageUrl ? (
              <Dragger
                name="file"
                multiple={false}
                beforeUpload={handleFileUpload}
                fileList={fileList}
                accept="image/*"
                showUploadList={false}
                style={{ minHeight: 180 }}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined style={{ fontSize: 48, color: "#1890ff" }} />
                </p>
                <p className="ant-upload-text">
                  Click or drag image file to upload
                </p>
                <p className="ant-upload-hint">
                  Support for JPG, PNG, GIF, WebP formats. Max size: 5MB
                </p>
              </Dragger>
            ) : (
              <div>
                <div style={{ marginBottom: 16 }}>
                  <S3Image
                    src={selectedImageUrl?.imageUrl}
                    alt="Selected"
                    width="100%"
                    style={{
                      maxHeight: 200,
                      objectFit: "cover",
                      borderRadius: 4,
                    }}
                  />
                </div>
                <Button onClick={handleFileRemove} type="dashed" block>
                  Remove Image
                </Button>
              </div>
            )}
          </Form.Item>

          {/* <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Enter photo description or title" />
          </Form.Item> */}

          <Form.Item
            name="order"
            label="Order"
            rules={[{ required: true, message: "Please enter order value" }]}
          >
            <InputNumber
              min={0}
              step={0.1}
              placeholder="Enter order value (supports decimals)"
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BannerTab;
